import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreateManager, useUpdateManager } from '@/hooks/useManagers';
import { ManagerForm } from '@/pages/clients/components/ManagerForm';
import type { Manager, ManagerInput } from '@jobos/shared/types/manager';

interface AddManagerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onManagerCreated?: (manager: Manager) => void;
  onManagerUpdated?: (manager: Manager) => void;
  clientId: string;
  editingManager?: Manager | null;
}

export function AddManagerModal({
  open,
  onOpenChange,
  onManagerCreated,
  onManagerUpdated,
  clientId,
  editingManager = null,
}: AddManagerModalProps) {
  const { mutateAsync: createManager, isPending: isCreating } = useCreateManager();
  const { mutateAsync: updateManager, isPending: isUpdating } = useUpdateManager(editingManager?.id || '');

  const isPending = isCreating || isUpdating;

  const handleSubmit = async (data: ManagerInput) => {
    try {
      if (editingManager) {
        const manager = await updateManager(data);
        onOpenChange(false);
        onManagerUpdated?.(manager);
      } else {
        const manager = await createManager(data);
        onOpenChange(false);
        onManagerCreated?.(manager);
      }
    } catch (error) {
      // error handling handled by hook toast; keep modal open for retry
      console.error('Failed to save manager:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editingManager ? 'Edit Manager' : 'Add Manager'}
          </DialogTitle>
          <DialogDescription>
            {editingManager
              ? 'Update the manager information below.'
              : 'Add a new manager for this client.'}
          </DialogDescription>
        </DialogHeader>
        <ManagerForm
          clientId={clientId}
          onSubmit={handleSubmit}
          defaultValues={editingManager ? {
            firstName: editingManager.firstName || undefined,
            lastName: editingManager.lastName || undefined,
            email: editingManager.email || undefined,
            phone: editingManager.phone || undefined,
          } : undefined}
          isPending={isPending}
          submitButtonText={editingManager ? 'Update Manager' : 'Create Manager'}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
